// ─── React & Libraries ───────────────────────────────────────
import { useCallback, useMemo, useState } from "react";
import { Controller, useFormContext } from "react-hook-form";
import { useTranslation } from "react-i18next";

// ─── UI Components ──────────────────────────────────────────
import TextInput from "../../hook_forms/hook_textInput";
import PrimaryButton from "../../common/PrimaryButton";
import Spinner from "../../common/Spinner";
import { ToastContainer, toast } from "react-toastify";

// ─── API Services ───────────────────────────────────────────
import {
  addNewOrganization,
  addNewOrganizationUser,
  addOrganizationFeeTemplate,
} from "../../../setup/api/services/organization";

// ─── Styles ─────────────────────────────────────────────────
import styles from "./AddOrganisation.module.scss";

// ─────────────────── Zustand Store ───────────────────────────
import { useOrganisationStore } from "../../../providers/organisation/organisation.provider";
import { uploadFile } from "../../../s3Integration/s3Client";
import { EUploadImageType } from "../../../s3Integration/enums";
import { getUserId } from "../../../setup/auth/oidc";
import PhoneInput from "../../hook_forms/hook_phoneInput/PhoneInput";

// ─── Props ──────────────────────────────────────────────────
interface Props {
  onNext: (password?: string) => void;
}

// ─── StepTwo Component ──────────────────────────────────────
// Handles admin information and submits the organization form.
const StepTwo = ({ onNext }: Props) => {
  const { t } = useTranslation(); // ✅ For translations

  // React Hook Form methods
  const {
    register,
    watch,
    control,
    getValues,
    formState: { errors },
  } = useFormContext();

  const [loading, setLoading] = useState(false);

  // ─── Zustand Selectors ─────────────────────────────────────
  const selectedOrganisation = useOrganisationStore(
    (state) => state.selectedOrganisation
  );
  const setSelectedOrganization = useOrganisationStore(
    (state) => state.setSelectedOrganization
  );
  const fetchOrganizations = useOrganisationStore(
    (state) => state.fetchOrganizations
  );

  // Watch values
  const adminName = watch("adminName");
  const email = watch("email");
  const mobile = watch("mobile");

  const userId = getUserId();

  const uploadImage = useCallback(
    (logo: File): Promise<string> => {
      // eslint-disable-next-line no-async-promise-executor
      return new Promise(async (resolve, reject) => {
        try {
          if (logo && userId) {
            const url = await uploadFile(
              logo,
              EUploadImageType.PROFILE,
              userId
            );
            resolve(url);
          } else {
            reject(new Error("Logo file is null"));
          }
        } catch (error) {
          reject(error);
        }
      });
    },
    [userId]
  );

  // ─── Submit Handler ───────────────────────────────────────
  const handleSubmitStepTwo = useCallback(async () => {
    const formValues = getValues();
    const {
      name,
      code,
      websiteUrl,
      logo,
      description,
      adminName,
      email,
      mobile,
    } = formValues;

    setLoading(true);
    let logoUrl = "";

    if (typeof logo !== "string") {
      if (logo && logo.length > 0 && logo[0]) {
        try {
          logoUrl = await uploadImage(logo[0]);
        } catch (error) {
          logoUrl = "";
        }
      } else {
        logoUrl = "";
      }
    } else {
      logoUrl = logo;
    }
    const payload = {
      name,
      code,
      // fees,
      logoUrl: logoUrl || "",
      websiteUrl,
      description,
      mappedStatus: true,
      manualWithdrawEnabled: true,
    };

    try {
      const firstRes = await addNewOrganization(payload);
      const parts = adminName.trim().split(" ");
      const body = {
        email,
        firstName: parts[0],
        lastName: parts.slice(1).join(" "),
        roles: ["CLIENT_ADMIN"],
        organizationCode: code,
        phoneNumber: `+966${mobile}`,
        clientUserId: `+966${mobile}`,
        clientId: firstRes?.data?.defaultPortalClientId,
      };
      const secondRequest = addNewOrganizationUser(body);

      const thirdRequest = addOrganizationFeeTemplate(firstRes?.data?.code, {
        name: `Default ${name} Fee Template`,
        description: `Default fee template for ${name}`,
        active: true,
        tiers: [
          {
            name: `Default ${name} tier`,
            from: 0,
          },
        ],
      });

      const [secondRes] = await Promise.all([secondRequest, thirdRequest]);

      onNext(secondRes?.data?.clientUserToken);
      toast(t("organization_saved_successfully"));
      fetchOrganizations();
    } catch (error) {
      console.error(error);
      toast(t("organization_save_error"));
    } finally {
      setLoading(false);
      setSelectedOrganization();
    }
  }, [
    getValues,
    onNext,
    setSelectedOrganization,
    t,
    uploadImage,
    fetchOrganizations,
  ]);

  // No longer auto-submit StepTwo for Edit flow

  // ─── Validation Check ─────────────────────────────────────
  const isNextDisabled = useMemo(() => {
    return (
      !adminName ||
      adminName.length < 2 ||
      !email ||
      !email.includes("@") ||
      !mobile
    );
  }, [adminName, email, mobile]);

  // ─── Render ───────────────────────────────────────────────
  return (
    <div className={styles.step_container}>
      {/* Admin Name */}
      {!selectedOrganisation?.code && (
        <>
          <TextInput
            {...register("adminName")}
            placeholder={t("administrator_name")}
            label={t("administrator_name")}
            errorMessage={errors.adminName?.message?.toString()}
          />

          {/* Email */}

          <TextInput
            {...register("email")}
            placeholder={t("email")}
            label={t("email")}
            errorMessage={errors.email?.message?.toString()}
          />

          {/* Mobile Number */}

          <Controller
            name="mobile"
            control={control}
            defaultValue=""
            render={({ field, fieldState }) => (
              <PhoneInput
                {...field}
                label={t("mobile_number")}
                errorMessage={fieldState.error?.message}
              />
            )}
          />
        </>
      )}

      {/* Submit */}
      <div className={styles.button_container}>
        <PrimaryButton
          color="primary"
          onClick={handleSubmitStepTwo}
          disabled={loading || isNextDisabled}>
          {loading ? <Spinner /> : t("lbl_add")}
        </PrimaryButton>
      </div>

      <ToastContainer />
    </div>
  );
};

export default StepTwo;
